const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

let accessToken = null;

export const setAccessToken = (token) => {
  accessToken = token;
};

export const getAccessToken = () => {
  return accessToken;
};

export const clearAccessToken = () => {
  accessToken = null;
};

/**
 * @description Refreshes the access token using refresh token from httpOnly cookie.
 * @returns {Promise<string>} New access token
 * @throws {Error} Throws error if refresh fails
 */
export const refreshAccessToken = async () => {
  try {
    const response = await fetch(`${API_URL}/api/auth/refresh`, {
      method: "POST",
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to refresh token");
    }

    const data = await response.json();
    setAccessToken(data.accessToken);
    return data.accessToken;
  } catch (error) {
    clearAccessToken();
    throw error;
  }
};

/**
 * @description Fetch wrapper that automatically adds authorization header and handles token refresh on 401 errors.
 * @param {string} url - Request URL
 * @param {Object} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise<Response>} Fetch response object
 * @throws {Error} Throws error if refresh fails after 401
 */
const fetchWithAuth = async (url, options = {}) => {
  const token = getAccessToken();

  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  let response = await fetch(url, {
    ...options,
    headers,
    credentials: "include",
  });

  if (response.status === 401 && token) {
    try {
      const newToken = await refreshAccessToken();
      const refreshedHeaders = {
        "Content-Type": "application/json",
        ...options.headers,
        Authorization: `Bearer ${newToken}`,
      };
      const retryOptions = {
        method: options.method || "GET",
        headers: refreshedHeaders,
        credentials: "include",
      };
      if (options.body && options.method !== "GET") {
        retryOptions.body = options.body;
      }
      response = await fetch(url, retryOptions);
    } catch (error) {
      clearAccessToken();
      throw error;
    }
  }

  return response;
};

/**
 * @description Checks if email address is available for registration.
 * @param {string} email - Email address to check
 * @returns {Promise<Object>} Response object with availability status
 * @throws {Error} Throws error if email check fails
 */
export const checkEmail = async (email) => {
  const response = await fetch(`${API_URL}/api/auth/check-email`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || "Email check failed");
  }

  return data;
};

/**
 * @description Registers a new user account.
 * @param {string} email - User email address
 * @param {string} confirmEmail - Email confirmation
 * @param {string} password - User password
 * @param {string} confirmPassword - Password confirmation
 * @param {string} company_name - Company name
 * @param {string} contact_name - Contact person name
 * @param {string} phone_number - Phone number
 * @param {string} website - Website URL (optional)
 * @returns {Promise<Object>} Response object with success message and user data
 * @throws {Error} Throws error if registration fails
 */
export const registerUser = async (
  email,
  confirmEmail,
  password,
  confirmPassword,
  company_name,
  contact_name,
  phone_number,
  website
) => {
  const response = await fetch(`${API_URL}/api/auth/register`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email,
      confirmEmail,
      password,
      confirmPassword,
      company_name,
      contact_name,
      phone_number,
      website: website || null,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || "Registration failed");
  }

  return data;
};

/**
 * @description Authenticates user and stores access token in memory.
 * @param {string} email - User email address
 * @param {string} password - User password
 * @returns {Promise<Object>} Response object with access token and user data
 * @throws {Error} Throws error if login fails
 */
export const loginUser = async (email, password) => {
  const response = await fetch(`${API_URL}/api/auth/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ email, password }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || "Login failed");
  }

  if (data.accessToken) {
    setAccessToken(data.accessToken);
  }

  return data;
};

/**
 * @description Logs out user and clears access token from memory.
 * @returns {Promise<void>}
 */
export const logoutUser = async () => {
  try {
    await fetch(`${API_URL}/api/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch (error) {
    console.error("Logout error:", error);
  } finally {
    clearAccessToken();
  }
};

/**
 * @description Verifies user email address using token from verification link.
 * @param {string} token - Email verification token
 * @param {string} email - User email address
 * @returns {Promise<Object>} Response object with success message
 * @throws {Error} Throws error if verification fails
 */
export const verifyEmail = async (token, email) => {
  const response = await fetch(
    `${API_URL}/api/auth/verify-email?token=${encodeURIComponent(
      token
    )}&email=${encodeURIComponent(email)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || "Email verification failed");
  }

  return data;
};

/**
 * @description Updates authenticated user profile information.
 * @param {Object} updatedValues - Object containing fields to update (company_name, contact_name, phone_number, website)
 * @returns {Promise<Object>} Updated user object
 * @throws {Error} Throws error if update fails
 */
export const updateUser = async (updatedValues) => {
  const response = await fetchWithAuth(`${API_URL}/api/user`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(updatedValues),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || "Update failed");
  }

  return data;
};

/**
 * @description Requests password reset link to be sent via email.
 * @param {string} email - User email address
 * @returns {Promise<Object>} Response object with success message
 * @throws {Error} Throws error if request fails
 */
export const requestPasswordReset = async (email) => {
  const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || "Password reset request failed");
  }

  return data;
};

/**
 * @description Confirms password reset using token from email link.
 * @param {string} email - User email address
 * @param {string} token - Password reset token
 * @param {string} newPassword - New password
 * @param {string} confirmPassword - Password confirmation
 * @returns {Promise<Object>} Response object with success message
 * @throws {Error} Throws error if reset fails
 */
export const confirmPasswordReset = async (
  email,
  token,
  newPassword,
  confirmPassword
) => {
  const response = await fetch(`${API_URL}/api/auth/reset-password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email,
      token,
      password: newPassword,
      confirmPassword,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || "Password reset failed");
  }

  return data;
};

/**
 * @description Changes password for authenticated user.
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 * @param {string} confirmPassword - Password confirmation
 * @returns {Promise<Object>} Response object with success message
 * @throws {Error} Throws error if password change fails
 */
export const changePassword = async (
  currentPassword,
  newPassword,
  confirmPassword
) => {
  const response = await fetchWithAuth(`${API_URL}/api/auth/change-password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      currentPassword,
      newPassword,
      confirmPassword,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    const errorMessage = data.message || "Error changing password.";
    throw new Error(errorMessage);
  }

  return data;
};

/**
 * @description Retrieves all questionnaire questions.
 * @returns {Promise<Array>} Array of question objects
 * @throws {Error} Throws error if request fails
 */
export const getQuestions = async () => {
  const response = await fetchWithAuth(`${API_URL}/api/questions`, {
    method: "GET",
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || "Error loading questions");
  }

  return data;
};

/**
 * @description Retrieves all answers for the authenticated user.
 * @returns {Promise<Array>} Array of answer objects
 * @throws {Error} Throws error if request fails
 */
export const getUserAnswers = async () => {
  const response = await fetchWithAuth(`${API_URL}/api/answers`, {
    method: "GET",
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || "Error loading answers");
  }

  return data;
};

/**
 * @description Saves or updates an answer for a question.
 * @param {number} questionId - Question ID
 * @param {boolean} answerValue - Answer value (true/false)
 * @returns {Promise<Object>} Response object with success message
 * @throws {Error} Throws error if save fails
 */
export const saveAnswer = async (questionId, answerValue) => {
  const response = await fetchWithAuth(`${API_URL}/api/answers`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      questionId,
      answerValue,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || "Error saving answer");
  }

  return data;
};

/**
 * @description Updates an existing answer for a question.
 * @param {number} questionId - Question ID
 * @param {boolean} answerValue - Answer value (true/false)
 * @returns {Promise<Object>} Response object with success message
 * @throws {Error} Throws error if update fails
 */
export const updateAnswer = async (questionId, answerValue) => {
  const response = await fetchWithAuth(`${API_URL}/api/answers/${questionId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      answerValue,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || "Error updating answer");
  }

  return data;
};

/**
 * @description Submits completed questionnaire and calculates results.
 * @returns {Promise<Object>} Response object with dimensions, levelProgress, blockingQuestions, overallLevel, and isAllDimensionsComplete
 * @throws {Error} Throws error if submission fails
 */
export const submitFragebogen = async () => {
  const response = await fetchWithAuth(`${API_URL}/api/fragebogen/submit`, {
    method: "POST",
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || "Error completing the questionnaire");
  }

  return data;
};

/**
 * @description Retrieves all users (admin only).
 * @returns {Promise<Array>} Array of user objects
 * @throws {Error} Throws error if request fails or user is not admin
 */
export const getAdminUsers = async () => {
  const response = await fetchWithAuth(`${API_URL}/api/admin/users`, {
    method: "GET",
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || "Error loading users");
  }

  return data;
};

/**
 * @description Retrieves admin statistics (admin only).
 * @returns {Promise<Object>} Statistics object
 * @throws {Error} Throws error if request fails or user is not admin
 */
export const getAdminStats = async () => {
  const response = await fetchWithAuth(`${API_URL}/api/admin/stats`, {
    method: "GET",
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || "Error loading statistics");
  }

  return data;
};

export { fetchWithAuth, API_URL };
